<template>
  <div class="h-screen w-full bg-white flex flex-col">
    <!-- Nav -->
    <div class="flex-shrink-0 h-14">
      <nav-menu />
    </div>

    <!-- Toolbar -->
    <div class="flex-shrink-0 bg-gray-50 border-b border-gray-200 px-6 py-3">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-lg font-semibold text-gray-900 mr-2">AI Planner</h1>

        <!-- Date Picker -->
        <div class="flex items-center">
          <label class="text-xs text-gray-500 mr-2">Date</label>
          <flat-pickr
            class="bg-white border border-gray-200 text-gray-800 rounded-lg px-3 text-sm w-40 outline-none"
            style="height:40px"
            :config="dateConfig"
            v-model="selectedDate"
          ></flat-pickr>
        </div>

        <!-- Tester Selector -->
        <div class="relative">
          <button
            @click="showTesterPanel = !showTesterPanel"
            class="bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 rounded-lg px-4 text-sm flex items-center transition-colors"
            style="height:40px"
          >
            <i class="fa-solid fa-users mr-2 text-primary"></i>
            {{ selectedTesterCount }} / {{ contractors.length }} Testers
            <i class="fa-solid fa-chevron-down ml-2 text-xs text-gray-400"></i>
          </button>

          <div
            v-show="showTesterPanel"
            class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-80"
          >
            <div class="p-3 border-b border-gray-200">
              <input
                type="text"
                v-model="testerSearch"
                placeholder="Search testers..."
                class="w-full bg-gray-50 text-gray-800 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
              />
            </div>
            <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <button @click="selectAllTesters" class="text-xs text-primary hover:underline">Select All</button>
              <span class="text-xs text-gray-500">{{ selectedTesterCount }} selected</span>
              <button @click="deselectAllTesters" class="text-xs text-red-500 hover:underline">Deselect All</button>
            </div>
            <div class="max-h-60 overflow-y-auto">
              <div
                v-for="c in filteredContractors"
                :key="c.id"
                @click="toggleContractor(c.id)"
                class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <div
                  :class="[
                    'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mr-3 transition-colors',
                    enabledContractors[c.id]
                      ? 'bg-primary border-primary'
                      : 'border-gray-300 bg-transparent'
                  ]"
                >
                  <i v-if="enabledContractors[c.id]" class="fa-solid fa-check text-white text-xs"></i>
                </div>
                <span class="text-sm text-gray-700 truncate">{{ c.name || c.id }}</span>
              </div>
              <div v-if="filteredContractors.length === 0" class="px-3 py-4 text-center text-xs text-gray-400">
                No testers match "{{ testerSearch }}"
              </div>
            </div>
            <div class="p-2 border-t border-gray-200">
              <button @click="showTesterPanel = false" class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded-lg transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>

        <!-- Status Filter -->
        <div class="relative">
          <button
            @click="showStatusPanel = !showStatusPanel"
            class="bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 rounded-lg px-4 text-sm flex items-center transition-colors"
            style="height:40px"
          >
            <i class="fa-solid fa-filter mr-2 text-primary"></i>
            {{ selectedStatusLabel }}
            <i class="fa-solid fa-chevron-down ml-2 text-xs text-gray-400"></i>
          </button>

          <div
            v-show="showStatusPanel"
            class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-56"
          >
            <div class="p-2">
              <div
                v-for="s in availableStatuses"
                :key="s.value"
                @click="toggleStatus(s.value)"
                class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded"
              >
                <div
                  :class="[
                    'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mr-3 transition-colors',
                    enabledStatuses[s.value]
                      ? 'bg-primary border-primary'
                      : 'border-gray-300 bg-transparent'
                  ]"
                >
                  <i v-if="enabledStatuses[s.value]" class="fa-solid fa-check text-white text-xs"></i>
                </div>
                <span :class="['text-sm mr-2 px-1.5 py-0.5 rounded', s.pillClass]">{{ s.label }}</span>
              </div>
            </div>
            <div class="p-2 border-t border-gray-200 flex gap-2">
              <button @click="selectAllStatuses" class="flex-grow text-xs text-primary hover:underline text-center">All</button>
              <button @click="showStatusPanel = false" class="flex-grow bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs py-1.5 rounded-lg transition-colors">Done</button>
            </div>
          </div>
        </div>

        <!-- Region Filter -->
        <div v-if="availableRegions.length > 0" class="relative">
          <button
            @click="showRegionPanel = !showRegionPanel"
            class="bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 rounded-lg px-4 text-sm flex items-center transition-colors"
            style="height:40px"
          >
            <i class="fa-solid fa-map mr-2 text-primary"></i>
            {{ selectedRegionCount === availableRegions.length ? 'All Regions' : selectedRegionCount + ' Region' + (selectedRegionCount !== 1 ? 's' : '') }}
            <i class="fa-solid fa-chevron-down ml-2 text-xs text-gray-400"></i>
          </button>

          <div
            v-show="showRegionPanel"
            class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-72"
          >
            <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <button @click="selectAllRegions" class="text-xs text-primary hover:underline">Select All</button>
              <span class="text-xs text-gray-500">{{ selectedRegionCount }} selected</span>
              <button @click="deselectAllRegions" class="text-xs text-red-500 hover:underline">Deselect All</button>
            </div>
            <div class="max-h-60 overflow-y-auto">
              <div
                v-for="r in availableRegions"
                :key="r.id"
                @click="toggleRegion(r.id)"
                class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <div
                  :class="[
                    'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mr-3 transition-colors',
                    enabledRegions[r.id]
                      ? 'bg-primary border-primary'
                      : 'border-gray-300 bg-transparent'
                  ]"
                >
                  <i v-if="enabledRegions[r.id]" class="fa-solid fa-check text-white text-xs"></i>
                </div>
                <div
                  v-if="r.color"
                  class="w-3 h-3 rounded-full flex-shrink-0 mr-2"
                  :style="{ backgroundColor: r.color }"
                ></div>
                <span class="text-sm text-gray-700 truncate">{{ r.name }}</span>
              </div>
              <div v-if="availableRegions.length === 0" class="px-3 py-4 text-center text-xs text-gray-400">
                No regions defined
              </div>
            </div>
            <div class="p-2 border-t border-gray-200">
              <button @click="showRegionPanel = false" class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded-lg transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>

        <!-- Starts Override -->
        <div class="relative">
          <button
            @click="showStartsPanel = !showStartsPanel"
            class="bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 rounded-lg px-4 text-sm flex items-center transition-colors"
            style="height:40px"
          >
            <i class="fa-solid fa-location-dot mr-2 text-primary"></i>
            Starts
            <span
              v-if="startOverrideCount > 0"
              class="ml-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold"
            >{{ startOverrideCount }}</span>
            <i class="fa-solid fa-chevron-down ml-2 text-xs text-gray-400"></i>
          </button>

          <div
            v-show="showStartsPanel"
            class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-96"
          >
            <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <span class="text-xs text-gray-500 font-bold">Driver Starting Locations</span>
              <button
                v-if="startOverrideCount > 0"
                @click="clearAllStartOverrides"
                class="text-xs text-red-500 hover:underline"
              >Clear All Overrides</button>
            </div>
            <div class="max-h-72 overflow-y-auto">
              <div
                v-for="c in activeContractors"
                :key="'start-' + c.id"
                class="flex items-center px-3 py-2 hover:bg-gray-50 border-b border-gray-100"
              >
                <div class="flex-grow min-w-0">
                  <div class="text-sm text-gray-700 truncate">{{ c.name || c.id }}</div>
                  <div class="text-xs truncate" :class="startLocationOverrides[c.id] ? 'text-primary' : 'text-gray-400'">
                    {{ getStartLabel(c) }}
                  </div>
                </div>
                <div v-if="editingStartOverride === c.id" class="flex-shrink-0 ml-2 relative">
                  <input
                    type="text"
                    v-model="startOverrideQuery"
                    @input="debounceStartOverrideGeocode"
                    placeholder="Search address..."
                    class="h-7 bg-gray-50 border border-gray-200 text-gray-800 rounded px-2 text-xs w-48 outline-none"
                    ref="startOverrideInput"
                  />
                  <button @click="editingStartOverride = null" class="ml-1 text-gray-400 hover:text-gray-600 text-xs">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                  <div v-if="startOverrideResults.length" class="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-64 max-h-40 overflow-y-auto">
                    <div
                      v-for="(result, idx) in startOverrideResults"
                      :key="idx"
                      @click="selectStartOverride(c, result)"
                      class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs text-gray-700 border-b border-gray-100 last:border-0"
                    >
                      {{ result.place_name }}
                    </div>
                  </div>
                </div>
                <div v-else class="flex-shrink-0 ml-2 flex items-center">
                  <button
                    @click="openStartOverrideEditor(c)"
                    class="text-gray-400 hover:text-primary text-xs"
                    title="Override starting location"
                  >
                    <i class="fa-solid fa-pencil"></i>
                  </button>
                  <button
                    v-if="startLocationOverrides[c.id]"
                    @click="clearStartOverride(c.id)"
                    class="ml-2 text-gray-400 hover:text-red-500 text-xs"
                    title="Remove override"
                  >
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </div>
              <div v-if="activeContractors.length === 0" class="px-3 py-4 text-center text-xs text-gray-400">
                No active testers selected
              </div>
            </div>
            <div class="p-2 border-t border-gray-200">
              <button @click="showStartsPanel = false" class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded-lg transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>

        <!-- Mode Toggle -->
        <div v-if="hasCurrentPlan" class="flex bg-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          <button
            @click="viewMode = 'current'"
            :class="['px-3 py-2 text-sm', viewMode === 'current' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900']"
          >Current Plan</button>
          <button
            @click="viewMode = 'ai'"
            :class="['px-3 py-2 text-sm', viewMode === 'ai' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900']"
          >AI Draft</button>
        </div>

        <div class="flex-grow"></div>

        <!-- Job count -->
        <div v-if="jobs.length" class="text-xs text-gray-500">
          {{ jobs.length }} jobs &middot; {{ filteredJobs.length }} eligible
          <span v-if="unassignedJobs.length"> &middot; {{ unassignedJobs.length }} unassigned</span>
          <span v-if="geocodedCount"> &middot; {{ geocodedCount }} geocoded</span>
        </div>

        <!-- See Draft Plan Button -->
        <button
          @click="showDraftModal = true"
          :disabled="optimizing || !filteredJobs.length || !selectedTesterCount"
          :class="[
            'px-5 rounded-lg text-sm font-bold flex items-center transition-all',
            optimizing || !filteredJobs.length || !selectedTesterCount
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-primary hover:opacity-90 text-white'
          ]"
          style="height:40px"
        >
          <i :class="['fa-solid mr-2', optimizing ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles']"></i>
          {{ optimizing ? 'Building Plan...' : 'See Draft Plan' }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-grow flex overflow-hidden" style="min-height: 0">
      <!-- Left Panel: Schedule Table (60%) -->
      <div class="w-3/5 flex flex-col overflow-hidden border-r border-gray-200">
        <!-- Loading state -->
        <div v-if="loading" class="flex-grow flex items-center justify-center">
          <div class="text-center">
            <i class="fa-solid fa-spinner fa-spin text-3xl text-primary mb-3"></i>
            <div class="text-gray-500 text-sm">Loading jobs...</div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="!jobs.length && !loading" class="flex-grow flex items-center justify-center">
          <div class="text-center">
            <i class="fa-regular fa-calendar text-5xl text-gray-300 mb-3"></i>
            <div class="text-gray-500">No jobs found for this date</div>
            <div class="text-gray-400 text-sm mt-1">Select a date with scheduled jobs</div>
          </div>
        </div>

        <!-- Optimizing state -->
        <div v-else-if="optimizing" class="flex-grow flex items-center justify-center">
          <div class="text-center">
            <i class="fa-solid fa-route fa-spin text-3xl text-primary mb-3"></i>
            <div class="text-gray-500 text-sm">Distributing {{ filteredJobs.length }} jobs across {{ selectedTesterCount }} testers...</div>
            <div v-if="optimizingStatus" class="text-primary text-sm mt-2 font-medium">{{ optimizingStatus }}</div>
            <div v-else class="text-gray-400 text-xs mt-1">Geocoding addresses & building distance matrix</div>
          </div>
        </div>

        <!-- Schedule Table -->
        <div v-else-if="displayAssignments" class="flex-grow overflow-y-auto">
          <!-- Focus mode banner -->
          <div v-if="focusedContractorId && displayAssignments[focusedContractorId]"
            class="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
            <span class="text-sm text-blue-800 font-medium">
              <i class="fa-solid fa-filter mr-1"></i>
              Showing {{ displayAssignments[focusedContractorId].contractor.name }} only
            </span>
            <button @click="clearFocus()" class="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-100">
              Show All Testers
            </button>
          </div>

          <!-- Unassigned jobs warning -->
          <div v-if="viewMode === 'current' && unassignedJobs.length > 0"
            class="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center">
            <i class="fa-solid fa-triangle-exclamation text-yellow-500 mr-2"></i>
            <span class="text-sm text-yellow-800">{{ unassignedJobs.length }} jobs have no tester assigned</span>
          </div>

          <!-- Time conflict warnings -->
          <div v-if="visibleTimeConflicts.length > 0"
            class="bg-red-50 border-b border-red-200 px-4 py-2">
            <div
              @click="conflictsCollapsed = !conflictsCollapsed"
              class="flex items-center cursor-pointer"
            >
              <i class="fa-solid fa-clock text-red-500 mr-2"></i>
              <span class="text-sm text-red-800 font-medium flex-grow">{{ visibleTimeConflicts.length }} time conflict{{ visibleTimeConflicts.length > 1 ? 's' : '' }} detected</span>
              <button @click.stop="dismissAllConflicts" class="text-xs text-red-400 hover:text-red-600 mr-2 px-2 py-0.5 rounded hover:bg-red-100">
                Dismiss All
              </button>
              <i :class="['fa-solid text-red-400 text-xs ml-2', conflictsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
            </div>
            <div v-show="!conflictsCollapsed">
              <div v-for="(c, ci) in visibleTimeConflicts" :key="ci" class="flex items-center text-xs text-red-600 ml-6 mt-1">
                <span class="flex-grow">{{ c.text }}</span>
                <button @click.stop="dismissConflict(ci)" class="text-red-300 hover:text-red-500 ml-2 flex-shrink-0">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-for="(a, cid) in filteredDisplayAssignments" :key="cid" class="border-b border-gray-200">
            <!-- Contractor Header -->
            <div
              @click="focusTester(cid)"
              :class="[
                'flex items-center px-4 py-3 cursor-pointer sticky top-0 z-10 transition-colors',
                focusedContractorId === cid ? 'bg-blue-100 ring-2 ring-primary' : 'bg-gray-50 hover:bg-gray-100'
              ]"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                :style="{ backgroundColor: contractorColorMap[cid] || '#6b7280' }"
              >
                {{ initials(a.contractor.name) }}
              </div>
              <div class="ml-3 flex-grow">
                <div class="font-bold text-sm">{{ a.contractor.name }}</div>
                <div class="text-xs text-gray-500">
                  {{ a.jobs.length }} jobs
                  <span v-if="a.totalDriveTime"> &middot; {{ formatDuration(a.totalDriveTime) }} driving</span>
                  &middot; {{ formatDuration(a.totalOnSiteTime) }} on-site
                  &middot; {{ contractorTotalMeters(a) }}m total
                </div>
                <div v-if="testerTimes[cid]" class="text-xs text-gray-500">
                  &#9654; {{ testerTimes[cid].startTime }} &rarr; &#127937; {{ testerTimes[cid].finishTime }}
                  ({{ testerTimes[cid].totalHours }}h)
                </div>
              </div>
              <i :class="['fa-solid text-gray-400 text-sm', expandedContractors[cid] ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
            </div>

            <!-- Job Rows -->
            <div v-show="expandedContractors[cid]">
              <div v-for="j in a.jobs" :key="j.job.id">
                <div
                  @click="highlightJob(j.job, cid)"
                  :class="[
                    'flex items-center px-4 py-2 border-b border-gray-100 hover:bg-blue-50 cursor-pointer text-sm transition-colors',
                    highlightedJobId === j.job.id ? 'bg-blue-50 ring-1 ring-primary ring-inset' : ''
                  ]"
                >
                  <!-- Conflict indicator -->
                  <i v-if="conflictJobIds.has(j.job.id)" class="fa-solid fa-triangle-exclamation text-red-500 text-xs flex-shrink-0 mr-1" :title="conflictJobIds.get(j.job.id).join('\n')"></i>

                  <!-- Order number -->
                  <div
                    class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    :style="{ backgroundColor: contractorColorMap[cid] || '#6b7280' }"
                  >
                    {{ j.order }}
                  </div>

                  <!-- Scheduled time -->
                  <div class="w-14 ml-3 text-xs text-gray-500 flex-shrink-0 font-medium">
                    {{ formatJobTime(j.job) }}
                  </div>

                  <!-- ETA column -->
                  <div v-if="estimatedArrival(a, j.order - 1)" class="w-14 text-xs text-gray-400 flex-shrink-0 font-mono" title="Estimated arrival">
                    <i class="fa-solid fa-arrow-right text-gray-300 mr-0.5" style="font-size:8px"></i>{{ estimatedArrival(a, j.order - 1) }}
                  </div>

                  <!-- SMS status icon -->
                  <div v-if="j.job.clientSmsStatus" class="flex-shrink-0 ml-2" :title="j.job.clientSmsStatus + ' via SMS'">
                    <i v-if="j.job.clientSmsStatus === 'FIRM'" class="fa-solid fa-circle-check text-green-500 cursor-pointer" @click="openChangeResolveModal(j.job)" title="FIRM via SMS – click to resolve"></i>
                    <i v-else-if="j.job.clientSmsStatus === 'CHANGED'" class="fa-solid fa-circle-question text-yellow-500 cursor-pointer" @click="openChangeResolveModal(j.job)" title="Click to resolve"></i>
                    <i v-else-if="j.job.clientSmsStatus === 'CANCELLED'" class="fa-solid fa-circle-xmark text-red-500 cursor-pointer" @click="openChangeResolveModal(j.job)" title="CANCELLED via SMS – click to resolve"></i>
                  </div>

                  <!-- Job info -->
                  <div class="flex-grow min-w-0 ml-2">
                    <div class="font-medium text-gray-900 truncate">
                      {{ j.job.company || j.job.projectName || j.job.projectId || 'Job' }}
                    </div>
                    <div class="text-xs text-gray-400 truncate">{{ formatJobAddress(j.job) }}</div>
                  </div>

                  <!-- Meters pill -->
                  <div v-if="j.meters" class="flex-shrink-0 ml-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                      {{ j.meters }}m
                    </span>
                  </div>

                  <!-- On-site time -->
                  <div v-if="j.onSiteTime" class="flex-shrink-0 ml-1">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">
                      <i class="fa-solid fa-clock text-xs mr-1"></i>{{ formatDuration(j.onSiteTime) }}
                    </span>
                  </div>

                  <!-- Drive time from prev -->
                  <div v-if="j.driveTimeFromPrev > 0" class="flex-shrink-0 ml-1">
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-0.5 rounded text-xs',
                        j.driveTimeFromPrev > 2700 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      ]"
                    >
                      <i class="fa-solid fa-car text-xs mr-1"></i>{{ formatDuration(j.driveTimeFromPrev) }}
                    </span>
                  </div>

                  <!-- Swap tester dropdown (read-only, no firebase) -->
                  <select
                    class="flex-shrink-0 ml-2 h-7 bg-gray-100 border border-gray-200 rounded text-xs px-1 w-28"
                    :value="cid"
                    @click.stop
                    @change="swapJobTester(j.job, cid, $event.target.value)"
                  >
                    <option v-for="c in allContractors" :key="c.id" :value="c.id">
                      {{ c.name || c.id }}
                    </option>
                  </select>

                  <!-- Remove tester button -->
                  <button
                    @click.stop="removeJobTester(j.job, cid)"
                    class="flex-shrink-0 ml-1 h-7 w-7 bg-red-100 hover:bg-red-200 text-red-500 rounded flex items-center justify-center"
                    title="Remove tester"
                  >
                    <i class="fa-solid fa-user-minus text-xs"></i>
                  </button>

                  <!-- SMS button -->
                  <button
                    @click.stop="openSmsModal(j.job)"
                    class="flex-shrink-0 ml-1 h-7 w-7 bg-indigo-100 hover:bg-indigo-200 text-indigo-600 rounded flex items-center justify-center"
                    title="Send SMS"
                  >
                    <i class="fa-solid fa-comment-sms text-xs"></i>
                  </button>
                  <!-- Client Link button -->
                  <button
                    @click.stop="openLinkModal(j.job)"
                    class="flex-shrink-0 ml-1 h-7 w-7 bg-green-100 hover:bg-green-200 text-green-600 rounded flex items-center justify-center"
                    title="Client Link"
                  >
                    <i class="fa-solid fa-link text-xs"></i>
                  </button>
                </div>

                <!-- Conflict detail banner for selected job -->
                <div
                  v-if="highlightedJobId === j.job.id && conflictJobIds.has(j.job.id)"
                  class="bg-red-50 border-l-4 border-red-400 px-4 py-2"
                >
                  <div
                    v-for="(msg, ci) in conflictJobIds.get(j.job.id)"
                    :key="ci"
                    class="flex items-center text-xs text-red-700 py-0.5"
                  >
                    <i class="fa-solid fa-triangle-exclamation text-red-500 mr-2 flex-shrink-0"></i>
                    {{ msg }}
                  </div>
                </div>

                <!-- Blue connector for flexible stacked jobs -->
                <div
                  v-if="stackedPairs.some(p => p.jobAId === j.job.id && p.contractorId === cid)"
                  class="flex items-center px-4 py-0"
                >
                  <div class="ml-3 w-6 flex justify-center">
                    <div class="w-0.5 h-6 bg-blue-500"></div>
                  </div>
                  <div class="ml-3 flex items-center">
                    <i class="fa-solid fa-link text-blue-500 text-xs mr-1"></i>
                    <span class="text-xs text-blue-500 font-medium">Flexible stack</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Waiting for date -->
        <div v-else class="flex-grow flex items-center justify-center">
          <div class="text-center">
            <i class="fa-solid fa-wand-magic-sparkles text-5xl text-gray-300 mb-3"></i>
            <div class="text-gray-400 text-sm">Select a date to view the schedule</div>
          </div>
        </div>
      </div>

      <!-- Right Panel (40%) -->
      <div class="w-2/5 flex flex-col overflow-hidden">
        <!-- Map -->
        <div class="flex-grow relative" style="min-height: 50%">
          <div ref="mapContainer" class="w-full h-full"></div>
        </div>

        <!-- Report Panel: Collapsed (click to open) -->
        <div
          v-if="displayEfficiency && !showReport"
          @click="showReport = true"
          class="flex-shrink-0 bg-gray-900 text-white px-4 py-2 flex items-center cursor-pointer hover:bg-gray-800 transition-colors border-t border-gray-700"
        >
          <i class="fa-solid fa-chart-simple text-primary mr-3 text-lg"></i>
          <div class="flex-grow">
            <div class="text-sm font-medium">Suggestions & Conflicts</div>
            <div class="text-xs text-gray-400">
              {{ displayEfficiency.totalDriveTimeFormatted }} driving &middot;
              {{ visibleInsights.length }} suggestions &middot;
              {{ visibleTimeConflicts.length }} conflicts
            </div>
          </div>
          <i class="fa-solid fa-chevron-up text-gray-400"></i>
        </div>

        <!-- Report Panel: Expanded -->
        <transition name="report-slide">
          <div v-if="displayEfficiency && showReport"
            class="flex-shrink-0 bg-gray-900 text-white p-4 overflow-y-auto border-t border-gray-700"
            style="max-height: 50%"
          >
            <!-- Header + Close -->
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm header-bold">
                <i class="fa-solid fa-chart-simple text-primary mr-2"></i>Suggestions & Conflicts
              </h3>
              <button @click="showReport = false" class="text-gray-400 hover:text-white flex-shrink-0">
                <i class="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            <!-- Summary Metrics -->
            <div class="grid grid-cols-3 gap-2 mb-3">
              <div class="bg-gray-800 rounded-lg p-2">
                <div class="text-xs text-gray-400">Drive Time</div>
                <div class="text-base font-bold">{{ displayEfficiency.totalDriveTimeFormatted }}</div>
              </div>
              <div class="bg-gray-800 rounded-lg p-2">
                <div class="text-xs text-gray-400">On-Site Time</div>
                <div class="text-base font-bold">{{ displayEfficiency.totalOnSiteTimeFormatted }}</div>
              </div>
              <div class="bg-gray-800 rounded-lg p-2">
                <div class="text-xs text-gray-400">Jobs / Tester</div>
                <div class="text-base font-bold">{{ displayEfficiency.avgJobsPerTester }}</div>
              </div>
            </div>

            <!-- Conflicts Section -->
            <div v-if="visibleTimeConflicts.length > 0" class="mb-3">
              <div class="flex items-center text-xs text-gray-400 font-bold mb-2">
                <i class="fa-solid fa-triangle-exclamation text-red-400 mr-1"></i>
                <span class="flex-grow">Conflicts ({{ visibleTimeConflicts.length }})</span>
                <button @click="dismissAllConflicts" class="text-xs text-red-400 hover:text-red-300 px-2 py-0.5 rounded hover:bg-gray-800">
                  Dismiss All
                </button>
              </div>
              <div v-for="(c, ci) in visibleTimeConflicts" :key="'conflict-'+ci"
                class="flex items-center bg-red-900 bg-opacity-30 rounded px-3 py-2 mb-1 text-xs text-red-300 border border-red-800">
                <span class="flex-grow"><i class="fa-solid fa-clock mr-1"></i>{{ c.text }}</span>
                <button @click.stop="dismissConflict(ci)" class="text-red-400 hover:text-red-200 ml-2 flex-shrink-0">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
            <div v-else class="mb-3">
              <div class="flex items-center text-xs text-green-400">
                <i class="fa-solid fa-check-circle mr-1"></i>
                No scheduling conflicts
              </div>
            </div>

            <!-- Tester Day Ends -->
            <div v-if="displayEfficiency.testerDayEnds && Object.keys(displayEfficiency.testerDayEnds).length" class="mb-3">
              <div class="text-xs text-gray-400 font-bold mb-1">
                <i class="fa-solid fa-flag-checkered mr-1"></i>Est. Day End
              </div>
              <div class="flex flex-wrap gap-2">
                <div v-for="(info, cid) in displayEfficiency.testerDayEnds" :key="cid"
                  class="bg-gray-800 rounded px-2 py-1 text-xs flex items-center">
                  <div
                    class="w-3 h-3 rounded-full mr-1.5 flex-shrink-0"
                    :style="{ backgroundColor: contractorColorMap[cid] || '#6b7280' }"
                  ></div>
                  <span class="text-gray-300 mr-1">{{ info.name }}</span>
                  <span class="font-bold text-white">{{ info.endTime }}</span>
                </div>
              </div>
            </div>

            <!-- Suggestions -->
            <div v-if="visibleInsights.length > 0">
              <div class="flex items-center mb-2">
                <div class="text-xs text-gray-400 font-bold">
                  <i class="fa-solid fa-lightbulb text-yellow-500 mr-1"></i>
                  Suggestions ({{ visibleInsights.length }})
                </div>
                <div class="ml-auto flex items-center gap-2">
                  <button
                    @click="dismissAllInsights"
                    class="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-800"
                  >
                    Dismiss All
                  </button>
                  <button
                    @click="acceptSelectedRecommendations"
                    :disabled="selectedInsightCount === 0"
                    :class="[
                      'text-xs px-2 py-1 rounded font-medium',
                      selectedInsightCount > 0
                        ? 'bg-primary text-white hover:bg-blue-600'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    ]"
                  >
                    Accept Selected ({{ selectedInsightCount }})
                  </button>
                  <button
                    @click="acceptAllRecommendations"
                    class="text-xs px-2 py-1 rounded font-medium bg-green-600 text-white hover:bg-green-700"
                  >
                    Accept All
                  </button>
                </div>
              </div>
              <div
                v-for="insight in visibleInsights"
                :key="insight._idx"
                class="flex items-start py-1.5 border-b border-gray-800 last:border-0"
              >
                <div
                  @click="toggleInsight(insight._idx)"
                  :class="[
                    'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mr-2 mt-0.5 cursor-pointer transition-colors',
                    selectedInsights[insight._idx]
                      ? 'bg-primary border-primary'
                      : insight.action ? 'border-gray-500 bg-transparent hover:border-primary' : 'border-gray-700 bg-transparent opacity-50'
                  ]"
                >
                  <i v-if="selectedInsights[insight._idx]" class="fa-solid fa-check text-white" style="font-size:8px"></i>
                </div>
                <span class="text-xs text-gray-300 flex-grow">
                  <i :class="[
                    'mr-1',
                    insight.action ? 'fa-solid fa-arrows-rotate text-primary' : 'fa-solid fa-info-circle text-gray-500'
                  ]"></i>
                  {{ insight.text }}
                </span>
                <button @click.stop="dismissInsight(insight._idx)" class="text-gray-600 hover:text-gray-400 ml-2 flex-shrink-0">
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              </div>
            </div>
            <div v-else-if="visibleInsights.length === 0" class="text-xs text-gray-500">
              <i class="fa-solid fa-check-circle text-green-400 mr-1"></i>No suggestions - schedule looks good.
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Footer -->
    <div v-if="displayAssignments && Object.keys(displayAssignments).length" class="flex-shrink-0 bg-gray-100 border-t border-gray-200 px-6 py-3">
      <div class="container mx-auto flex items-center justify-between">
        <div class="text-sm text-gray-500">
          {{ activeAssignmentCount }} testers &middot; {{ totalAssignedJobs }} jobs
          <span v-if="viewMode === 'ai'" class="ml-2 bg-primary text-white px-2 py-0.5 rounded text-xs">AI DRAFT</span>
          <span v-else class="ml-2 bg-gray-500 text-white px-2 py-0.5 rounded text-xs">CURRENT</span>
          <span v-if="hasLocalChanges" class="ml-2 bg-yellow-500 text-white px-2 py-0.5 rounded text-xs">MODIFIED</span>
        </div>
        <div class="flex items-center gap-3">
          <button
            @click="resetToCurrentPlan"
            v-if="hasLocalChanges"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-lg font-medium"
          >
            Undo Changes
          </button>
          <button
            @click="saveToSchedule"
            :disabled="publishing"
            :class="[
              'px-5 py-2 rounded-lg text-sm font-bold flex items-center',
              publishing ? 'bg-gray-400 text-white cursor-wait' : 'bg-green-600 hover:bg-green-700 text-white'
            ]"
          >
            <i :class="['fa-solid mr-2', publishing ? 'fa-spinner fa-spin' : 'fa-calendar-check']"></i>
            {{ publishing ? 'Saving...' : 'Save to Schedule' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Draft Plan Modal -->
    <div v-if="showDraftModal" class="fixed z-50 inset-0 flex items-center justify-center">
      <div class="absolute inset-0 z-10 bg-gray-900 opacity-75" @click="showDraftModal = false"></div>
      <div class="bg-white z-20 rounded-lg shadow-xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-gray-900">
            <i class="fa-solid fa-wand-magic-sparkles text-primary mr-2"></i>Build Draft Plan
          </h2>
          <span @click="showDraftModal = false" class="text-gray-400 hover:text-gray-600 cursor-pointer text-2xl">&times;</span>
        </div>

        <p class="text-sm text-gray-500 mb-4">Choose which jobs the AI should reassign:</p>

        <div class="space-y-3 mb-6">
          <label
            @click="draftJobScope = 'all'"
            :class="[
              'flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors',
              draftJobScope === 'all' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            ]"
          >
            <div
              :class="[
                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mr-3 mt-0.5',
                draftJobScope === 'all' ? 'border-primary' : 'border-gray-300'
              ]"
            >
              <div v-if="draftJobScope === 'all'" class="w-2.5 h-2.5 rounded-full bg-primary"></div>
            </div>
            <div>
              <div class="text-sm font-bold text-gray-900">All jobs ({{ filteredJobs.length }})</div>
              <div class="text-xs text-gray-500 mt-0.5">Reassign every job from scratch for optimal routes</div>
            </div>
          </label>

          <label
            @click="draftJobScope = 'unassigned'"
            :class="[
              'flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors',
              draftJobScope === 'unassigned' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            ]"
          >
            <div
              :class="[
                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mr-3 mt-0.5',
                draftJobScope === 'unassigned' ? 'border-primary' : 'border-gray-300'
              ]"
            >
              <div v-if="draftJobScope === 'unassigned'" class="w-2.5 h-2.5 rounded-full bg-primary"></div>
            </div>
            <div>
              <div class="text-sm font-bold text-gray-900">Unassigned only ({{ unassignedJobCount }})</div>
              <div class="text-xs text-gray-500 mt-0.5">Keep existing tester assignments, only assign jobs with no tester</div>
            </div>
          </label>
        </div>

        <!-- Planning Mode -->
        <div class="mb-4">
          <label class="text-xs text-gray-500 font-bold mb-2 block">Planning Mode</label>
          <div class="flex gap-3">
            <label
              @click="draftMode = 'optimal'"
              :class="[
                'flex-1 flex items-center p-3 rounded-lg border-2 cursor-pointer transition-colors text-sm',
                draftMode === 'optimal' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              ]"
            >
              <div
                :class="[
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mr-2',
                  draftMode === 'optimal' ? 'border-primary' : 'border-gray-300'
                ]"
              >
                <div v-if="draftMode === 'optimal'" class="w-2 h-2 rounded-full bg-primary"></div>
              </div>
              <div>
                <div class="font-bold text-gray-900">Optimal</div>
                <div class="text-xs text-gray-500">Best routes, minimise total drive time</div>
              </div>
            </label>
            <label
              @click="draftMode = 'roundrobin'"
              :class="[
                'flex-1 flex items-center p-3 rounded-lg border-2 cursor-pointer transition-colors text-sm',
                draftMode === 'roundrobin' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              ]"
            >
              <div
                :class="[
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mr-2',
                  draftMode === 'roundrobin' ? 'border-primary' : 'border-gray-300'
                ]"
              >
                <div v-if="draftMode === 'roundrobin'" class="w-2 h-2 rounded-full bg-primary"></div>
              </div>
              <div>
                <div class="font-bold text-gray-900">Round Robin</div>
                <div class="text-xs text-gray-500">Even job distribution across testers</div>
              </div>
            </label>
          </div>
        </div>

        <!-- Drive Constraints -->
        <div class="mb-4">
          <label class="text-xs text-gray-500 font-bold mb-2 block">Drive Constraints</label>
          <div class="flex gap-3">
            <div class="flex-1">
              <label class="text-xs text-gray-500 mb-1 block">Max daily drive per tester</label>
              <input
                type="number"
                v-model="maxDailyDrive"
                placeholder="No limit"
                min="0"
                class="w-full bg-gray-50 text-gray-800 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
              />
              <span class="text-xs text-gray-400">minutes</span>
            </div>
            <div class="flex-1">
              <label class="text-xs text-gray-500 mb-1 block">Max drive between jobs</label>
              <input
                type="number"
                v-model="maxSegmentDrive"
                placeholder="No limit"
                min="0"
                class="w-full bg-gray-50 text-gray-800 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none"
              />
              <span class="text-xs text-gray-400">minutes</span>
            </div>
          </div>
        </div>

        <!-- Review violations warning -->
        <div v-if="reviewViolations.length > 0" class="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div class="flex items-center text-sm text-yellow-800 font-medium mb-1">
            <i class="fa-solid fa-triangle-exclamation text-yellow-500 mr-2"></i>
            {{ reviewViolations.length }} rule violation{{ reviewViolations.length > 1 ? 's' : '' }} detected
          </div>
          <div v-for="(v, vi) in reviewViolations.slice(0, 5)" :key="vi" class="text-xs text-yellow-700 ml-6 mt-0.5">
            {{ v.tester }}: {{ v.detail }}
          </div>
          <div v-if="reviewViolations.length > 5" class="text-xs text-yellow-600 ml-6 mt-1">
            + {{ reviewViolations.length - 5 }} more
          </div>
        </div>

        <div class="flex items-center justify-between gap-3">
          <button
            @click="showRulesModal = true"
            class="text-gray-400 hover:text-primary text-sm flex items-center"
            title="Edit AI rules brief"
          >
            <i class="fa-solid fa-scroll mr-1"></i>
            Rules
          </button>
          <div class="flex items-center gap-3">
            <button
              @click="showDraftModal = false"
              class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
            >Cancel</button>
            <button
              @click="confirmDraftPlan"
              :disabled="draftJobScope === 'unassigned' && unassignedJobCount === 0"
              :class="[
                'px-5 py-2 rounded-lg text-sm font-bold flex items-center',
                draftJobScope === 'unassigned' && unassignedJobCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-blue-600 text-white'
              ]"
            >
              <i class="fa-solid fa-wand-magic-sparkles mr-2"></i>
              Build Plan
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Rules Modal -->
    <div v-if="showRulesModal" class="fixed z-50 inset-0 flex items-center justify-center">
      <div class="absolute inset-0 z-10 bg-gray-900 opacity-75" @click="showRulesModal = false"></div>
      <div class="bg-white z-20 rounded-lg shadow-xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-gray-900">
            <i class="fa-solid fa-scroll text-primary mr-2"></i>AI Rules Brief
          </h2>
          <span @click="showRulesModal = false" class="text-gray-400 hover:text-gray-600 cursor-pointer text-2xl">&times;</span>
        </div>
        <p class="text-xs text-gray-500 mb-3">These rules guide the AI planner when building draft plans.</p>
        <textarea
          v-model="plannerRules"
          rows="8"
          class="w-full bg-gray-50 text-gray-800 border border-gray-200 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-primary mb-4"
          placeholder="Enter planning rules..."
        ></textarea>
        <div class="flex items-center justify-end gap-3">
          <button
            @click="showRulesModal = false"
            class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
          >Close</button>
          <button
            @click="savePlannerRules()"
            :disabled="savingRules"
            :class="[
              'px-5 py-2 rounded-lg text-sm font-bold flex items-center',
              savingRules ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-primary hover:bg-blue-600 text-white'
            ]"
          >
            <i :class="['fa-solid mr-2', savingRules ? 'fa-spinner fa-spin' : 'fa-floppy-disk']"></i>
            {{ savingRules ? 'Saving...' : 'Save Rules' }}
          </button>
        </div>
      </div>
    </div>

    <!-- SMS Modal -->
    <div v-if="smsModal" class="fixed z-50 inset-0 flex items-center justify-center">
      <div class="absolute inset-0 z-10 bg-gray-900 opacity-60" @click="smsModal = false"></div>
      <div class="bg-white z-20 rounded-lg shadow-xl p-6 w-full max-w-lg max-h-screen overflow-y-auto relative">
        <span @click="smsModal = false" class="absolute top-3 right-4 text-gray-400 hover:text-gray-600 cursor-pointer text-2xl">&times;</span>

        <h2 class="text-lg font-bold text-gray-900 mb-4">
          <i class="fa-solid fa-comment-sms text-indigo-600 mr-2"></i>Send SMS
        </h2>

        <div class="mb-3">
          <label class="text-xs text-gray-500 mb-1 block">To</label>
          <input
            type="text"
            v-model="smsPhone"
            placeholder="+614XXXXXXXX"
            class="rounded h-10 bg-gray-100 w-full outline-none px-3 text-gray-600 text-sm"
          />
        </div>

        <div class="mb-3">
          <label class="text-xs text-gray-500 mb-1 block">Message</label>
          <textarea
            v-model="smsMessage"
            rows="4"
            class="rounded bg-gray-100 p-3 text-sm text-gray-600 w-full outline-none"
          ></textarea>
        </div>

        <button
          @click="sendJobSms()"
          :disabled="smsLoading"
          :class="[
            'w-full rounded flex items-center justify-center h-10 text-sm font-medium mb-4',
            smsLoading
              ? 'bg-gray-400 text-white cursor-wait'
              : 'bg-indigo-600 text-white hover:bg-indigo-800 cursor-pointer'
          ]"
        >
          <i v-if="smsLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          <i v-else class="fa-solid fa-paper-plane mr-2"></i>
          {{ smsLoading ? 'Sending...' : 'Send SMS' }}
        </button>

        <div v-if="smsHistory.length > 0" class="border-t border-gray-200 pt-3">
          <h3 class="text-sm font-semibold text-gray-600 mb-2">Message History</h3>
          <div
            v-for="(msg, idx) in smsHistory"
            :key="idx"
            class="mb-2 p-2 rounded text-xs"
            :class="msg.direction === 'inbound' ? 'bg-blue-50' : 'bg-gray-50'"
          >
            <div class="flex items-center justify-between mb-1">
              <span class="font-semibold" :class="msg.direction === 'inbound' ? 'text-blue-600' : 'text-gray-600'">
                {{ msg.direction === 'inbound' ? 'Received' : 'Sent' }}
              </span>
              <span v-if="msg.classification" class="text-xs px-2 py-0.5 rounded-full"
                :class="{
                  'bg-green-100 text-green-700': msg.classification === 'FIRM',
                  'bg-yellow-100 text-yellow-700': msg.classification === 'CHANGED',
                  'bg-red-100 text-red-700': msg.classification === 'CANCELLED',
                }"
              >{{ msg.classification }}</span>
            </div>
            <div class="text-gray-700">{{ msg.body }}</div>
            <div v-if="msg.timestamp" class="text-gray-400 mt-1 text-xs">
              {{ formatSmsTimestamp(msg.timestamp) }}
            </div>
          </div>
        </div>
        <div v-else-if="!smsLoading" class="text-xs text-gray-400 text-center pt-2">
          No message history for this job.
        </div>
      </div>
    </div>

    <!-- Client Link Modal -->
    <div v-if="linkModal" class="fixed z-50 inset-0 flex items-center justify-center">
      <div class="absolute inset-0 z-10 bg-gray-900 opacity-60" @click="linkModal = false"></div>
      <div class="bg-white z-20 rounded-lg shadow-xl p-6 w-full max-w-lg relative">
        <span @click="linkModal = false" class="absolute top-3 right-4 text-gray-400 hover:text-gray-600 cursor-pointer text-2xl">&times;</span>

        <h2 class="text-lg font-bold text-gray-900 mb-4">
          <i class="fa-solid fa-link text-green-600 mr-2"></i>Client Job Link
        </h2>

        <div v-if="linkJob" class="mb-3 text-sm text-gray-500">
          <span class="font-medium text-gray-700">{{ linkJob.company }}</span>
          <span v-if="linkJob.formattedAddress"> — {{ linkJob.formattedAddress }}</span>
        </div>

        <div class="flex items-center mb-4">
          <input
            type="text"
            :value="linkUrl"
            readonly
            class="rounded-l h-10 bg-gray-100 flex-1 outline-none px-3 text-gray-600 text-sm border border-r-0 border-gray-200"
            @click="$event.target.select()"
          />
          <button
            @click="copyLink"
            class="h-10 px-4 rounded-r text-sm font-medium flex items-center"
            :class="linkCopied ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-800'"
          >
            <i :class="linkCopied ? 'fa-solid fa-check' : 'fa-regular fa-copy'" class="mr-1"></i>
            {{ linkCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Click-away overlay for tester panel -->
    <div v-if="showTesterPanel" class="fixed inset-0 z-40" @click="showTesterPanel = false"></div>
    <!-- Click-away overlay for status panel -->
    <div v-if="showStatusPanel" class="fixed inset-0 z-40" @click="showStatusPanel = false"></div>
    <!-- Click-away overlay for starts panel -->
    <div v-if="showStartsPanel" class="fixed inset-0 z-40" @click="showStartsPanel = false"></div>
    <!-- Click-away overlay for region panel -->
    <div v-if="showRegionPanel" class="fixed inset-0 z-40" @click="showRegionPanel = false"></div>

    <!-- Change Resolve Modal -->
    <div v-if="changeResolveModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4">Resolve Change Request</h3>
        <div v-if="changeResolveJob && changeResolveJob.clientSmsMessage" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p class="text-xs text-gray-500 mb-1">Client message</p>
          <p class="text-sm text-gray-800 italic">"{{ changeResolveJob.clientSmsMessage }}"</p>
        </div>
        <textarea v-model="changeResolveNote" rows="3" class="w-full border border-gray-300 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-primary mb-4" placeholder="Resolution note (optional)"></textarea>
        <div class="flex space-x-3">
          <button @click="changeResolveModal = false; changeResolveJob = null; changeResolveNote = ''" class="flex-1 h-10 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50">Cancel</button>
          <button @click="resolveChangeRequest()" class="flex-1 h-10 rounded-lg bg-primary text-white font-medium hover:opacity-90">Mark as Resolved</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import NavMenu from "@/components/headers/navMenu";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

import {
  getJobsByDate,
  getContractors,
  getVehicles,
  setFire
} from "../firestore/utils";

import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, orderBy, doc, getDoc } from "firebase/firestore";
import axios from "axios";

import {
  getJobCoords,
  formatJobAddress as formatAddr,
  toDate,
  shouldExcludeJob,
  getJobMeters,
  geocodeMissingJobs,
  getContractorStart,
  buildDurationMatrix,
  buildCurrentAssignments,
  assignJobsEqual,
  reviewAssignments,
  calculateEfficiency,
  calculateTesterTimes,
  detectTimeConflicts,
  findStackedPairs,
  selfOptimize,
  clearCache,
  formatDuration as fmtDur,
  getContractorColor
} from "../utils/routeOptimizer";

const MAPBOX_TOKEN = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";

export default {
  components: { NavMenu, flatPickr },

  data() {
    return {
      selectedDate: null,
      dateConfig: { enableTime: false, altInput: true, allowInput: false, dateFormat: 'Y-m-d' },
      jobs: [],
      contractors: [],
      allContractors: [], // all contractors for swap dropdown
      vehicles: [],
      enabledContractors: {},
      showTesterPanel: false,
      showStatusPanel: false,
      testerSearch: '',
      availableStatuses: [
        { value: 'FIRM', label: 'Firm', pillClass: 'bg-green-700 text-green-100' },
        { value: 'ON-CALL', label: 'On-Call', pillClass: 'bg-yellow-700 text-yellow-100' },
        { value: 'COMMITTED', label: 'Committed', pillClass: 'bg-blue-700 text-blue-100' },
      ],
      enabledStatuses: { 'FIRM': true, 'ON-CALL': true, 'COMMITTED': true },

      // Current plan (from existing job.contractor data)
      currentAssignments: null,
      currentEfficiency: null,
      unassignedJobs: [],

      // AI plan
      aiAssignments: null,
      aiEfficiency: null,
      timeConflicts: [],

      // Which view is active
      viewMode: 'current', // 'current' or 'ai'

      // Local modifications tracking
      hasLocalChanges: false,

      changeResolveModal: false,
      changeResolveJob: null,
      changeResolveNote: '',
      conflictsCollapsed: false,
      loading: false,
      optimizing: false,
      publishing: false,
      expandedContractors: {},
      highlightedJobId: null,
      selectedInsights: {},
      geocodedCount: 0,
      map: null,
      mapMarkers: [],       // { marker, contractorId, jobId? }
      mapRoutes: [],         // { sourceId, layerId, contractorId }
      contractorColorMap: {},
      focusedContractorId: null,  // when a tester header or job is clicked, isolate this contractor
      showReport: false,
      lastDurationMatrix: null,
      lastNumContractors: 0,
      optimizingStatus: '',
      showStartsPanel: false,
      startLocationOverrides: {},
      editingStartOverride: null,
      startOverrideQuery: '',
      startOverrideResults: [],
      startOverrideTimer: null,
      showDraftModal: false,
      draftJobScope: 'all',
      testerTimes: {},
      smsModal: false,
      smsJob: null,
      smsPhone: '',
      smsMessage: '',
      smsHistory: [],
      smsLoading: false,
      linkModal: false,
      linkJob: null,
      linkUrl: '',
      linkCopied: false,
      stackedPairs: [],
      dismissedConflicts: {},
      dismissedInsights: {},

      // Region filtering
      availableRegions: [],
      enabledRegions: {},
      showRegionPanel: false,

      // Planning mode & drive constraints
      draftMode: 'optimal',
      maxDailyDrive: '',
      maxSegmentDrive: '',

      // AI rules brief
      plannerRules: '',
      showRulesModal: false,
      savingRules: false,

      // Double-review violations
      reviewViolations: [],
    };
  },

  computed: {
    selectedRegionCount() {
      return Object.values(this.enabledRegions).filter(Boolean).length;
    },
    selectedRegionNames() {
      const names = new Set();
      for (const [id, enabled] of Object.entries(this.enabledRegions)) {
        if (!enabled) continue;
        const region = this.availableRegions.find(r => r.id === id);
        if (region) names.add(region.name);
      }
      return names;
    },
    filteredJobs() {
      let jobs = this.jobs.filter(j => !shouldExcludeJob(j) && this.enabledStatuses[j.status]);
      if (this.selectedRegionCount > 0 && this.selectedRegionCount < this.availableRegions.length) {
        const names = this.selectedRegionNames;
        jobs = jobs.filter(j => j.region && names.has(j.region));
      }
      return jobs;
    },
    selectedStatusLabel() {
      const enabled = Object.entries(this.enabledStatuses).filter(([, v]) => v);
      if (enabled.length === this.availableStatuses.length) return 'All Statuses';
      if (enabled.length === 0) return 'No Status';
      return enabled.map(([k]) => k === 'ON-CALL' ? 'On-Call' : k.charAt(0) + k.slice(1).toLowerCase()).join(', ');
    },
    filteredContractors() {
      if (!this.testerSearch) return this.contractors;
      const q = this.testerSearch.toLowerCase();
      return this.contractors.filter(c =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.id && c.id.toLowerCase().includes(q))
      );
    },
    selectedTesterCount() {
      return Object.values(this.enabledContractors).filter(Boolean).length;
    },
    activeContractors() {
      let list = this.contractors.filter(c => this.enabledContractors[c.id]);
      if (this.selectedRegionCount > 0 && this.selectedRegionCount < this.availableRegions.length) {
        const names = this.selectedRegionNames;
        list = list.filter(c => c.region && names.has(c.region));
      }
      return list;
    },
    hasCurrentPlan() {
      return this.currentAssignments !== null && this.aiAssignments !== null;
    },
    displayAssignments() {
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!source) return null;
      const result = {};
      for (const [cid, a] of Object.entries(source)) {
        if (a.jobs.length > 0) result[cid] = a;
      }
      return Object.keys(result).length > 0 ? result : null;
    },
    filteredDisplayAssignments() {
      const all = this.displayAssignments;
      if (!all) return null;
      if (!this.focusedContractorId) return all;
      if (all[this.focusedContractorId]) {
        return { [this.focusedContractorId]: all[this.focusedContractorId] };
      }
      return all;
    },
    conflictJobIds() {
      const map = new Map();
      for (const c of this.timeConflicts) {
        if (!map.has(c.jobAId)) map.set(c.jobAId, []);
        if (!map.has(c.jobBId)) map.set(c.jobBId, []);
        map.get(c.jobAId).push(c.text);
        map.get(c.jobBId).push(c.text);
      }
      return map;
    },
    displayEfficiency() {
      return this.viewMode === 'ai' ? this.aiEfficiency : this.currentEfficiency;
    },
    activeAssignmentCount() {
      const a = this.displayAssignments;
      return a ? Object.keys(a).length : 0;
    },
    totalAssignedJobs() {
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!source) return 0;
      return Object.values(source).reduce((sum, a) => sum + a.jobs.length, 0);
    },
    unassignedJobCount() {
      return this.filteredJobs.filter(j => !j.contractor || !j.contractor.id).length;
    },
    startOverrideCount() {
      return Object.keys(this.startLocationOverrides).length;
    },
    selectedInsightCount() {
      const eff = this.displayEfficiency;
      if (!eff) return 0;
      let count = 0;
      for (const [idx, selected] of Object.entries(this.selectedInsights)) {
        if (selected && eff.insights[idx] && eff.insights[idx].action) count++;
      }
      return count;
    },
    visibleTimeConflicts() {
      return this.timeConflicts.filter((_, i) => !this.dismissedConflicts[i]);
    },
    visibleInsights() {
      if (!this.displayEfficiency) return [];
      return this.displayEfficiency.insights
        .map((insight, idx) => ({ ...insight, _idx: idx }))
        .filter(i => !this.dismissedInsights[i._idx]);
    },
  },

  watch: {
    selectedDate() { this.loadJobs(); },
    viewMode() {
      this.selectedInsights = {};
      // Recalculate conflicts for the active view
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (source) {
        this.timeConflicts = detectTimeConflicts(source);
      } else {
        this.timeConflicts = [];
      }
      this.$nextTick(() => {
        if (source) this.renderMap(source, this.allContractors);
      });
    }
  },

  methods: {
    openChangeResolveModal(job) {
      this.changeResolveJob = job;
      this.changeResolveNote = '';
      this.changeResolveModal = true;
    },
    async resolveChangeRequest() {
      if (!this.changeResolveJob) return;
      await setFire({ collection: "jobs", doc: this.changeResolveJob.id, body: { clientSmsStatus: null } });
      this.changeResolveModal = false;
      this.changeResolveJob = null;
      this.changeResolveNote = '';
    },
    formatDuration: fmtDur,
    formatJobAddress: formatAddr,
    getContractorColor,

    getStartLabel(contractor) {
      if (this.startLocationOverrides[contractor.id]) {
        return 'Override: ' + (this.startLocationOverrides[contractor.id].address || 'Custom');
      }
      if (contractor.startLocation && contractor.startLocation.address) {
        return contractor.startLocation.address;
      }
      return 'Default (Yatala)';
    },
    openStartOverrideEditor(contractor) {
      this.editingStartOverride = contractor.id;
      this.startOverrideQuery = '';
      this.startOverrideResults = [];
      this.$nextTick(() => {
        if (this.$refs.startOverrideInput) {
          const input = Array.isArray(this.$refs.startOverrideInput) ? this.$refs.startOverrideInput[0] : this.$refs.startOverrideInput;
          if (input) input.focus();
        }
      });
    },
    debounceStartOverrideGeocode() {
      if (this.startOverrideTimer) clearTimeout(this.startOverrideTimer);
      this.startOverrideTimer = setTimeout(() => { this.geocodeStartOverride(); }, 300);
    },
    async geocodeStartOverride() {
      const q = this.startOverrideQuery.trim();
      if (q.length < 3) { this.startOverrideResults = []; return; }
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=au&limit=5`;
        const response = await fetch(url);
        const data = await response.json();
        this.startOverrideResults = data.features || [];
      } catch (e) {
        console.error('Geocode error:', e);
        this.startOverrideResults = [];
      }
    },
    selectStartOverride(contractor, result) {
      const [lng, lat] = result.center;
      this.$set(this.startLocationOverrides, contractor.id, { lat, lng, address: result.place_name });
      this.editingStartOverride = null;
      this.startOverrideResults = [];
    },
    clearStartOverride(contractorId) {
      this.$delete(this.startLocationOverrides, contractorId);
    },
    clearAllStartOverrides() {
      this.startLocationOverrides = {};
    },

    // --- Region methods ---
    async fetchAvailableRegions() {
      try {
        const q = query(collection(db, 'regions'), orderBy('name'));
        const snapshot = await getDocs(q);
        const regions = [];
        const enabled = {};
        snapshot.forEach(d => {
          const data = { id: d.id, ...d.data() };
          regions.push(data);
          enabled[d.id] = true;
        });
        this.availableRegions = regions;
        this.enabledRegions = enabled;
      } catch (e) {
        console.error('Error fetching regions:', e);
      }
    },

    async fetchPlannerRules() {
      try {
        const docRef = doc(db, 'settings', 'plannerRules');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().rules) {
          this.plannerRules = docSnap.data().rules;
        } else {
          this.plannerRules = '- Testers must only be assigned jobs within their designated region\n- Minimise total driving time across all testers\n- Avoid scheduling conflicts between overlapping jobs\n- Balance workload evenly across testers where possible';
        }
      } catch (e) {
        console.error('Error fetching planner rules:', e);
        this.plannerRules = '- Testers must only be assigned jobs within their designated region\n- Minimise total driving time across all testers\n- Avoid scheduling conflicts between overlapping jobs\n- Balance workload evenly across testers where possible';
      }
    },

    async savePlannerRules() {
      this.savingRules = true;
      try {
        await setFire({ collection: 'settings', doc: 'plannerRules', body: { rules: this.plannerRules } });
        this.showRulesModal = false;
      } catch (e) {
        console.error('Error saving planner rules:', e);
        alert('Failed to save rules: ' + e.message);
      }
      this.savingRules = false;
    },

    toggleRegion(regionId) {
      this.$set(this.enabledRegions, regionId, !this.enabledRegions[regionId]);
    },
    selectAllRegions() {
      const enabled = {};
      this.availableRegions.forEach(r => { enabled[r.id] = true; });
      this.enabledRegions = enabled;
    },
    deselectAllRegions() {
      const enabled = {};
      this.availableRegions.forEach(r => { enabled[r.id] = false; });
      this.enabledRegions = enabled;
    },

    initials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },

    formatJobTime(job) {
      const d = toDate(job.date);
      if (!d) return '--:--';
      return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    },

    contractorTotalMeters(a) {
      return a.jobs.reduce((sum, j) => sum + (j.meters || 0), 0);
    },

    toggleStatus(status) { this.$set(this.enabledStatuses, status, !this.enabledStatuses[status]); },
    selectAllStatuses() {
      const enabled = {};
      this.availableStatuses.forEach(s => { enabled[s.value] = true; });
      this.enabledStatuses = enabled;
    },
    toggleContractor(cid) { this.$set(this.enabledContractors, cid, !this.enabledContractors[cid]); },
    selectAllTesters() {
      const enabled = {};
      this.contractors.forEach(c => { enabled[c.id] = true; });
      this.enabledContractors = enabled;
    },
    deselectAllTesters() {
      const enabled = {};
      this.contractors.forEach(c => { enabled[c.id] = false; });
      this.enabledContractors = enabled;
    },
    toggleAccordion(cid) { this.$set(this.expandedContractors, cid, !this.expandedContractors[cid]); },

    toggleInsight(idx) {
      const eff = this.displayEfficiency;
      if (!eff || !eff.insights[idx] || !eff.insights[idx].action) return;
      this.$set(this.selectedInsights, idx, !this.selectedInsights[idx]);
    },

    dismissConflict(index) {
      this.$set(this.dismissedConflicts, index, true);
    },
    dismissInsight(index) {
      this.$set(this.dismissedInsights, index, true);
    },
    dismissAllConflicts() {
      const dismissed = {};
      this.timeConflicts.forEach((_, i) => { dismissed[i] = true; });
      this.dismissedConflicts = dismissed;
    },
    dismissAllInsights() {
      if (!this.displayEfficiency) return;
      const dismissed = {};
      this.displayEfficiency.insights.forEach((_, i) => { dismissed[i] = true; });
      this.dismissedInsights = dismissed;
    },

    acceptAllRecommendations() {
      const eff = this.displayEfficiency;
      if (!eff) return;
      const selected = {};
      eff.insights.forEach((insight, idx) => {
        if (insight.action) selected[idx] = true;
      });
      this.selectedInsights = selected;
      this.applySelectedInsights();
    },

    acceptSelectedRecommendations() {
      this.applySelectedInsights();
    },

    applySelectedInsights() {
      const eff = this.displayEfficiency;
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!eff || !source) return;

      const toApply = [];
      for (const [idx, selected] of Object.entries(this.selectedInsights)) {
        if (!selected) continue;
        const insight = eff.insights[idx];
        if (insight && insight.action) toApply.push(insight);
      }
      if (!toApply.length) return;

      for (const insight of toApply) {
        const action = insight.action;
        if (action.type === 'swap') {
          this.executeSwap(source, action.jobAId, action.cidA, action.jobBId, action.cidB);
        } else if (action.type === 'consolidate') {
          for (const move of action.moves) {
            this.executeMove(source, move.jobId, move.fromCid, move.toCid);
          }
        }
      }

      this.recalculateAfterChanges(source);
      this.hasLocalChanges = true;
    },

    executeSwap(assignments, jobAId, cidA, jobBId, cidB) {
      const aA = assignments[cidA];
      const aB = assignments[cidB];
      if (!aA || !aB) return;
      const aIdx = aA.jobs.findIndex(j => j.job.id === jobAId);
      const bIdx = aB.jobs.findIndex(j => j.job.id === jobBId);
      if (aIdx === -1 || bIdx === -1) return;
      const jobA = aA.jobs[aIdx];
      const jobB = aB.jobs[bIdx];
      aA.jobs.splice(aIdx, 1, jobB);
      aB.jobs.splice(bIdx, 1, jobA);
    },

    executeMove(assignments, jobId, fromCid, toCid) {
      const from = assignments[fromCid];
      const to = assignments[toCid];
      if (!from || !to) return;
      const idx = from.jobs.findIndex(j => j.job.id === jobId);
      if (idx === -1) return;
      const [removed] = from.jobs.splice(idx, 1);
      to.jobs.push(removed);
    },

    swapJobTester(job, fromCid, toCid) {
      if (fromCid === toCid) return;
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!source) return;

      // Ensure target contractor bucket exists
      if (!source[toCid]) {
        const contractor = this.allContractors.find(c => c.id === toCid);
        if (!contractor) return;
        this.$set(source, toCid, {
          contractor,
          jobs: [],
          totalDriveTime: 0,
          totalOnSiteTime: 0,
        });
      }

      // Preserve focus state across recalculation
      const savedFocus = this.focusedContractorId;
      this.executeMove(source, job.id, fromCid, toCid);
      this.recalculateAfterChanges(source);
      this.focusedContractorId = savedFocus;
      this.hasLocalChanges = true;
    },

    removeJobTester(job, fromCid) {
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!source || !source[fromCid]) return;

      const idx = source[fromCid].jobs.findIndex(j => j.job.id === job.id);
      if (idx === -1) return;
      source[fromCid].jobs.splice(idx, 1);

      // Add to unassigned list
      this.unassignedJobs.push(job);

      // Preserve focus state across recalculation
      const savedFocus = this.focusedContractorId;
      this.recalculateAfterChanges(source);
      this.focusedContractorId = savedFocus;
      this.hasLocalChanges = true;
    },

    recalculateAfterChanges(assignments) {
      const sortByTime = (a, b) => {
        const tA = toDate(a.job.date);
        const tB = toDate(b.job.date);
        return (tA ? tA.getTime() : Infinity) - (tB ? tB.getTime() : Infinity);
      };

      for (const cid of Object.keys(assignments)) {
        const a = assignments[cid];
        a.jobs.sort(sortByTime);
        a.jobs.forEach((j, i) => { j.order = i + 1; });
        a.totalDriveTime = a.jobs.reduce((sum, j) => sum + (j.driveTimeFromPrev || 0), 0);
        a.totalOnSiteTime = a.jobs.reduce((sum, j) => sum + (j.onSiteTime || 0), 0);
        this.$set(assignments, cid, { ...a });
      }

      const activeContractors = this.allContractors.filter(c => assignments[c.id] && assignments[c.id].jobs.length > 0);
      const eff = calculateEfficiency(
        assignments, activeContractors,
        this.lastDurationMatrix, this.lastNumContractors, null
      );

      if (this.viewMode === 'ai') {
        this.aiEfficiency = eff;
      } else {
        this.currentEfficiency = eff;
      }
      this.testerTimes = calculateTesterTimes(assignments);
      this.timeConflicts = detectTimeConflicts(assignments);
      this.stackedPairs = findStackedPairs(assignments);
      this.selectedInsights = {};
      this.dismissedConflicts = {};
      this.dismissedInsights = {};

      this.$nextTick(() => {
        this.renderMap(assignments, this.allContractors);
      });
    },

    async loadJobs() {
      if (!this.selectedDate) return;
      this.loading = true;
      this.resetAll();

      try {
        const dateFrom = new Date(this.selectedDate);
        dateFrom.setHours(0, 0, 0, 0);
        const dateTo = new Date(this.selectedDate);
        dateTo.setHours(23, 59, 59, 999);

        const [jobs, contractors, vehicles] = await Promise.all([
          getJobsByDate({ collection: 'jobs', dateFrom, dateTo }),
          getContractors(),
          getVehicles()
        ]);

        this.jobs = jobs.filter(j => j.status !== 'CANCELLED');
        this.vehicles = vehicles;
        this.allContractors = contractors.filter(c => c.role === 'contractor');
        this.contractors = this.allContractors;

        const enabled = {};
        this.contractors.forEach(c => { enabled[c.id] = true; });
        this.enabledContractors = enabled;

        // Load regions and planner rules
        this.fetchAvailableRegions();
        this.fetchPlannerRules();

        // Build current plan from existing job.contractor assignments
        const eligible = this.filteredJobs;
        if (eligible.length > 0) {
          const { assignments, unassigned } = buildCurrentAssignments(eligible, this.allContractors);
          this.currentAssignments = assignments;
          this.unassignedJobs = unassigned;
          this.timeConflicts = detectTimeConflicts(assignments);
          this.stackedPairs = findStackedPairs(assignments);
          this.testerTimes = calculateTesterTimes(assignments);
          this.dismissedConflicts = {};
          this.dismissedInsights = {};

          // Build color map for contractors that have jobs
          const colorMap = {};
          this.allContractors.forEach((c, idx) => {
            colorMap[c.id] = getContractorColor(idx);
          });
          this.contractorColorMap = colorMap;

          // Efficiency report for current state
          const activeContractors = this.allContractors.filter(c => assignments[c.id] && assignments[c.id].jobs.length > 0);
          this.currentEfficiency = calculateEfficiency(
            assignments, activeContractors, null, null, null
          );

          // Expand all that have jobs
          const expanded = {};
          Object.keys(assignments).forEach(cid => {
            if (assignments[cid].jobs.length > 0) expanded[cid] = true;
          });
          this.expandedContractors = expanded;

          this.viewMode = 'current';

          this.$nextTick(() => {
            this.renderMap(assignments, this.allContractors);
          });
        }

      } catch (e) {
        console.error('Error loading jobs:', e);
      }
      this.loading = false;
    },

    confirmDraftPlan() {
      this.showDraftModal = false;
      this.runOptimizer(this.draftJobScope);
    },

    async runOptimizer(scope) {
      const active = this.activeContractors;
      const allEligible = this.filteredJobs;

      // If 'unassigned' scope, only optimize jobs without a tester;
      // keep existing assignments locked in place.
      const unassignedOnly = scope === 'unassigned';
      const eligible = unassignedOnly
        ? allEligible.filter(j => !j.contractor || !j.contractor.id)
        : allEligible;
      if (!eligible.length || !active.length) return;

      this.optimizing = true;
      this.aiAssignments = null;
      this.aiEfficiency = null;
      this.timeConflicts = [];
      this.selectedInsights = {};
      this.reviewViolations = [];

      try {
        // Geocode missing coords
        this.geocodedCount = await geocodeMissingJobs(eligible);

        // Separate jobs with/without coords for matrix
        const jobsWithCoords = [];
        const coordJobIndices = new Map(); // allJobIdx -> coordListIdx
        eligible.forEach((job, allIdx) => {
          if (getJobCoords(job)) {
            coordJobIndices.set(allIdx, jobsWithCoords.length);
            jobsWithCoords.push(job);
          }
        });

        // Build locations: [contractor starts..., coord jobs...]
        const locations = [];
        active.forEach(c => { locations.push(getContractorStart(c, this.vehicles, this.startLocationOverrides)); });
        jobsWithCoords.forEach(j => { locations.push(getJobCoords(j)); });

        // Build duration matrix (only for jobs with coords)
        let matrix = null;
        if (jobsWithCoords.length > 0 && locations.length > 1) {
          matrix = await buildDurationMatrix(locations);
        } else {
          // Fallback: zero matrix
          matrix = Array.from({ length: locations.length }, () => new Array(locations.length).fill(0));
        }
        this.lastDurationMatrix = matrix;
        this.lastNumContractors = active.length;

        // Build color map
        const colorMap = {};
        active.forEach((c, idx) => { colorMap[c.id] = getContractorColor(idx); });
        this.contractorColorMap = colorMap;

        // Build optimizer options
        const optimizerOptions = {
          mode: this.draftMode,
          maxDailyDriveSeconds: this.maxDailyDrive ? parseInt(this.maxDailyDrive) * 60 : null,
          maxSegmentDriveSeconds: this.maxSegmentDrive ? parseInt(this.maxSegmentDrive) * 60 : null,
          regionFilter: this.selectedRegionCount > 0,
        };

        // Run AI assignment on ALL eligible jobs
        const result = assignJobsEqual(eligible, active, coordJobIndices, locations, matrix, optimizerOptions);

        // Self-optimize: iterate until A+ or no actionable insights
        const currentDriveData = this.currentEfficiency ? { totalDriveTime: this.currentEfficiency.totalDriveTime } : null;
        const optimized = await selfOptimize(
          result.assignments, active, matrix, active.length, currentDriveData, 10,
          ({ pass, maxIter, grade }) => {
            this.optimizingStatus = `Optimizing... pass ${pass}/${maxIter} (${grade})`;
          }
        );

        let finalAssignments = optimized.assignments;

        // In 'unassigned only' mode, merge AI-assigned jobs into the existing current plan
        if (unassignedOnly && this.currentAssignments) {
          const merged = {};
          // Start with a copy of current assignments
          for (const cid of Object.keys(this.currentAssignments)) {
            const a = this.currentAssignments[cid];
            merged[cid] = {
              contractor: a.contractor,
              jobs: a.jobs.map(j => ({ ...j })),
              totalDriveTime: a.totalDriveTime,
              totalOnSiteTime: a.totalOnSiteTime,
            };
          }
          // Append AI-assigned jobs into their assigned contractor buckets
          for (const cid of Object.keys(finalAssignments)) {
            if (!merged[cid]) {
              merged[cid] = {
                contractor: finalAssignments[cid].contractor,
                jobs: [],
                totalDriveTime: 0,
                totalOnSiteTime: 0,
              };
            }
            for (const j of finalAssignments[cid].jobs) {
              merged[cid].jobs.push({ ...j });
            }
          }
          // Re-sort and recalculate
          const sortByTime = (a, b) => {
            const tA = toDate(a.job.date);
            const tB = toDate(b.job.date);
            return (tA ? tA.getTime() : Infinity) - (tB ? tB.getTime() : Infinity);
          };
          for (const cid of Object.keys(merged)) {
            const a = merged[cid];
            a.jobs.sort(sortByTime);
            a.jobs.forEach((j, i) => { j.order = i + 1; });
            a.totalDriveTime = a.jobs.reduce((sum, j) => sum + (j.driveTimeFromPrev || 0), 0);
            a.totalOnSiteTime = a.jobs.reduce((sum, j) => sum + (j.onSiteTime || 0), 0);
          }
          finalAssignments = merged;
        }

        // Double-review step: validate assignments against rules
        const review1 = reviewAssignments(finalAssignments, optimizerOptions);
        if (!review1.valid) {
          console.warn('Review pass 1 found violations:', review1.violations);
          // Second pass: re-run with stricter constraints for violating jobs
          const result2 = assignJobsEqual(eligible, active, coordJobIndices, locations, matrix, optimizerOptions);
          const optimized2 = await selfOptimize(
            result2.assignments, active, matrix, active.length, currentDriveData, 5
          );
          const review2 = reviewAssignments(optimized2.assignments, optimizerOptions);
          if (review2.valid) {
            finalAssignments = optimized2.assignments;
            this.reviewViolations = [];
          } else {
            console.warn('Review pass 2 still has violations:', review2.violations);
            this.reviewViolations = review2.violations;
          }
        } else {
          this.reviewViolations = [];
        }

        this.aiAssignments = finalAssignments;
        this.testerTimes = calculateTesterTimes(finalAssignments);
        const mergedContractors = this.allContractors.filter(c => finalAssignments[c.id] && finalAssignments[c.id].jobs.length > 0);
        this.aiEfficiency = calculateEfficiency(
          finalAssignments, mergedContractors,
          this.lastDurationMatrix, this.lastNumContractors,
          this.currentEfficiency ? { totalDriveTime: this.currentEfficiency.totalDriveTime } : null
        );
        this.timeConflicts = detectTimeConflicts(finalAssignments);
        this.stackedPairs = findStackedPairs(finalAssignments);
        this.dismissedConflicts = {};
        this.dismissedInsights = {};
        this.optimizingStatus = '';

        // Expand all
        const expanded = {};
        Object.keys(finalAssignments).forEach(cid => {
          if (finalAssignments[cid].jobs.length > 0) expanded[cid] = true;
        });
        this.expandedContractors = expanded;

        // Switch to AI view
        this.viewMode = 'ai';
        this.hasLocalChanges = false;

        this.$nextTick(() => {
          this.renderMap(finalAssignments, this.allContractors);
        });

      } catch (e) {
        console.error('Optimizer error:', e);
        alert('Error building plan: ' + e.message);
      }

      this.optimizing = false;
    },

    resetToCurrentPlan() {
      // Reload current assignments from the original job data
      const eligible = this.filteredJobs;
      const { assignments, unassigned } = buildCurrentAssignments(eligible, this.allContractors);
      this.currentAssignments = assignments;
      this.unassignedJobs = unassigned;

      const activeContractors = this.allContractors.filter(c => assignments[c.id] && assignments[c.id].jobs.length > 0);
      this.currentEfficiency = calculateEfficiency(assignments, activeContractors, null, null, null);
      this.testerTimes = calculateTesterTimes(assignments);

      this.hasLocalChanges = false;
      this.selectedInsights = {};
      this.dismissedConflicts = {};
      this.dismissedInsights = {};
      this.timeConflicts = detectTimeConflicts(assignments);
      this.stackedPairs = findStackedPairs(assignments);
      this.viewMode = 'current';

      this.$nextTick(() => {
        this.renderMap(assignments, this.allContractors);
      });
    },

    renderMap(assignments, contractors) {
      this.clearMapLayers();
      this.focusedContractorId = null;
      if (!this.map) return;

      const bounds = new mapboxgl.LngLatBounds();
      let hasPoints = false;

      contractors.forEach((c, idx) => {
        const a = assignments[c.id];
        if (!a || !a.jobs.length) return;

        const color = this.contractorColorMap[c.id] || getContractorColor(idx);
        const start = getContractorStart(c, this.vehicles, this.startLocationOverrides);

        const startEl = document.createElement('div');
        startEl.style.cssText = `
          width: 24px; height: 24px; border-radius: 50%;
          background: ${color}; border: 2px solid white;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; color: white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transition: opacity 0.3s, transform 0.3s;
        `;
        startEl.innerHTML = '<i class="fa-solid fa-house" style="font-size:10px"></i>';

        const startMarker = new mapboxgl.Marker({ element: startEl })
          .setLngLat([start.lng, start.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(`<b>${c.name}</b><br/>Start position`))
          .addTo(this.map);
        this.mapMarkers.push({ marker: startMarker, contractorId: c.id, jobId: null });
        bounds.extend([start.lng, start.lat]);
        hasPoints = true;

        const routeCoords = [[start.lng, start.lat]];

        a.jobs.forEach(j => {
          const coords = getJobCoords(j.job);
          if (!coords) return;

          const el = document.createElement('div');
          el.style.cssText = `
            width: 28px; height: 28px; border-radius: 50%;
            background: ${color}; border: 2px solid white;
            display: flex; align-items: center; justify-content: center;
            font-size: 12px; font-weight: bold; color: white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3); cursor: pointer;
            transition: opacity 0.3s, transform 0.3s;
          `;
          el.textContent = j.order;
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            this.highlightJob(j.job, c.id);
          });

          const jobName = j.job.company || j.job.projectName || j.job.projectId || 'Job';
          const addr = formatAddr(j.job);
          const meters = j.meters ? `<br/><span style="color:#a855f6">${j.meters}m</span>` : '';
          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat([coords.lng, coords.lat])
            .setPopup(new mapboxgl.Popup({ offset: 14 }).setHTML(
              `<div style="font-size:12px"><b>${j.order}. ${jobName}</b><br/><span style="color:#888">${addr}</span>${meters}</div>`
            ))
            .addTo(this.map);
          this.mapMarkers.push({ marker, contractorId: c.id, jobId: j.job.id });
          bounds.extend([coords.lng, coords.lat]);
          hasPoints = true;

          routeCoords.push([coords.lng, coords.lat]);
        });

        if (routeCoords.length > 1) {
          this.fetchAndDrawRoute(c.id, routeCoords, color);
        }
      });

      if (hasPoints) {
        this.map.fitBounds(bounds, { padding: 50, maxZoom: 13 });
      }
    },

    async fetchAndDrawRoute(contractorId, waypoints, color) {
      if (waypoints.length < 2) return;

      const maxWaypoints = 25;
      const allCoords = [];

      for (let i = 0; i < waypoints.length; i += maxWaypoints - 1) {
        const chunk = waypoints.slice(i, i + maxWaypoints);
        if (chunk.length < 2) break;

        const coordStr = chunk.map(w => `${w[0]},${w[1]}`).join(';');
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordStr}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;

        try {
          const response = await fetch(url);
          const data = await response.json();
          if (data.routes && data.routes[0]) {
            allCoords.push(...data.routes[0].geometry.coordinates);
          }
        } catch (e) {
          console.error('Route fetch error:', e);
          allCoords.push(...chunk);
        }
      }

      if (allCoords.length < 2) return;

      const sourceId = `route-${contractorId}`;
      const layerId = `route-layer-${contractorId}`;

      if (this.map.getSource(sourceId)) {
        this.map.getSource(sourceId).setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: allCoords }
        });
      } else {
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: allCoords }
          }
        });
        this.map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': color,
            'line-width': 4,
            'line-opacity': 0.8
          }
        });
        this.mapRoutes.push({ sourceId, layerId, contractorId });
      }
    },

    clearMapLayers() {
      this.mapMarkers.forEach(entry => entry.marker.remove());
      this.mapMarkers = [];
      if (this.map) {
        this.mapRoutes.forEach(r => {
          if (this.map.getLayer(r.layerId)) this.map.removeLayer(r.layerId);
          if (this.map.getSource(r.sourceId)) this.map.removeSource(r.sourceId);
        });
      }
      this.mapRoutes = [];
    },

    highlightJob(job, contractorId) {
      // If clicking the same job again, deselect
      if (this.highlightedJobId === job.id) {
        this.clearFocus();
        return;
      }

      this.highlightedJobId = job.id;

      // Find which contractor owns this job
      const cid = contractorId || this.findContractorForJob(job.id);
      if (!cid) return;

      this.focusedContractorId = cid;
      this.applyMapFocus(cid, job.id);

      // Fly to the selected job
      const coords = getJobCoords(job);
      if (coords && this.map) {
        this.map.flyTo({ center: [coords.lng, coords.lat], zoom: 13, duration: 800 });
      }
    },

    findContractorForJob(jobId) {
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!source) return null;
      for (const [cid, a] of Object.entries(source)) {
        if (a.jobs.some(j => j.job.id === jobId)) return cid;
      }
      return null;
    },

    clearFocus() {
      this.highlightedJobId = null;
      this.focusedContractorId = null;

      // Restore all markers and routes
      this.mapMarkers.forEach(entry => {
        const el = entry.marker.getElement();
        el.style.display = '';
        el.style.transform = '';
      });
      this.mapRoutes.forEach(entry => {
        if (this.map.getLayer(entry.layerId)) {
          this.map.setPaintProperty(entry.layerId, 'line-opacity', 0.8);
          this.map.setPaintProperty(entry.layerId, 'line-width', 4);
        }
      });
    },

    focusTester(cid) {
      if (this.focusedContractorId === cid) {
        // Already focused on this tester — toggle their job accordion
        this.$set(this.expandedContractors, cid, !this.expandedContractors[cid]);
        return;
      }
      this.focusedContractorId = cid;
      this.highlightedJobId = null;
      this.$set(this.expandedContractors, cid, true);
      this.applyMapFocus(cid, null);
    },

    estimatedArrival(a, jobIndex) {
      if (jobIndex === 0) return null;
      const prev = a.jobs[jobIndex - 1];
      const prevDate = toDate(prev.job.date);
      if (!prevDate) return null;
      const arrivalMs = prevDate.getTime() + (prev.onSiteTime * 1000) + ((a.jobs[jobIndex].driveTimeFromPrev || 0) * 1000);
      const d = new Date(arrivalMs);
      return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    },

    applyMapFocus(contractorId, selectedJobId) {
      // Hide all other contractors' markers, show focused contractor's
      this.mapMarkers.forEach(entry => {
        const el = entry.marker.getElement();

        if (entry.contractorId !== contractorId) {
          // Other contractor - hide completely
          el.style.display = 'none';
        } else {
          el.style.display = '';
          if (entry.jobId === selectedJobId) {
            el.style.transform = 'scale(1.4)';
          } else {
            el.style.transform = '';
          }
        }
      });

      // Hide other routes, thicken focused route
      this.mapRoutes.forEach(entry => {
        if (!this.map.getLayer(entry.layerId)) return;
        if (entry.contractorId === contractorId) {
          this.map.setPaintProperty(entry.layerId, 'line-opacity', 1);
          this.map.setPaintProperty(entry.layerId, 'line-width', 6);
        } else {
          this.map.setPaintProperty(entry.layerId, 'line-opacity', 0);
        }
      });
    },

    async saveToSchedule() {
      const source = this.viewMode === 'ai' ? this.aiAssignments : this.currentAssignments;
      if (!source) return;
      this.publishing = true;

      try {
        const promises = [];
        for (const [cid, a] of Object.entries(source)) {
          for (const j of a.jobs) {
            promises.push(
              setFire({
                collection: 'jobs',
                doc: j.job.id,
                body: {
                  contractor: { id: a.contractor.id, name: a.contractor.name }
                }
              })
            );
          }
        }
        await Promise.all(promises);

        alert(`Saved ${promises.length} jobs to schedule.`);
      } catch (e) {
        console.error('Save error:', e);
        alert('Some jobs failed to save: ' + e.message);
      }

      this.publishing = false;
    },

    resetAll() {
      this.currentAssignments = null;
      this.currentEfficiency = null;
      this.aiAssignments = null;
      this.aiEfficiency = null;
      this.unassignedJobs = [];
      this.timeConflicts = [];
      this.lastDurationMatrix = null;
      this.lastNumContractors = 0;
      this.expandedContractors = {};
      this.highlightedJobId = null;
      this.focusedContractorId = null;
      this.contractorColorMap = {};
      this.selectedInsights = {};
      this.dismissedConflicts = {};
      this.dismissedInsights = {};
      this.stackedPairs = [];
      this.geocodedCount = 0;
      this.hasLocalChanges = false;
      this.viewMode = 'current';
      this.reviewViolations = [];
      clearCache();
      this.clearMapLayers();
    },

    // --- SMS methods ---
    normalizePhoneForSms(phone) {
      if (!phone) return '';
      let p = phone.replace(/[\s\-\(\)]/g, '');
      if (p.startsWith('04')) p = '+61' + p.slice(1);
      else if (p.startsWith('614') && !p.startsWith('+')) p = '+' + p;
      else if (p.startsWith('61') && !p.startsWith('+')) p = '+' + p;
      return p;
    },

    getCurrentUser() {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          user ? resolve(user) : reject(new Error('No user is signed in.'));
        });
      });
    },

    async openSmsModal(job) {
      this.smsJob = job;
      this.smsPhone = this.normalizePhoneForSms(job.phone || '');
      const addr = job.formattedAddress || formatAddr(job) || '';
      const dateObj = toDate(job.date);
      const dateStr = dateObj
        ? dateObj.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '';
      this.smsMessage = `Hi, this is KCT. Please confirm your job at ${addr} on ${dateStr}. Reply YES to confirm or let us know if anything has changed.`;
      this.smsModal = true;
      this.smsHistory = [];
      if (job.id) {
        await this.loadSmsHistory(job.id);
      }
    },

    async loadSmsHistory(jobId) {
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await axios.get(
          `https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.smsHistory = res.data.data || res.data || [];
      } catch (e) {
        console.error('Failed to load SMS history:', e);
        this.smsHistory = [];
      }
    },

    async sendJobSms() {
      if (!this.smsPhone || !this.smsMessage || !this.smsJob) return;
      this.smsLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const jobId = this.smsJob.id;
        await axios.post(
          `https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/job/${jobId}`,
          { message: this.smsMessage, to: this.smsPhone },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (jobId) {
          await this.loadSmsHistory(jobId);
        }
        this.smsMessage = '';
      } catch (e) {
        console.error('Failed to send SMS:', e);
        alert('Failed to send SMS. Please try again.');
      } finally {
        this.smsLoading = false;
      }
    },

    async openLinkModal(job) {
      this.linkJob = job;
      this.linkCopied = false;
      const secret = 'kct-client-link-s3cret-2024';
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
      );
      const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(job.id));
      const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
      const token = `${job.id}~${hex.slice(0, 16)}`;
      this.linkUrl = `${window.location.origin}/job/${token}`;
      this.linkModal = true;
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.linkUrl);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      } catch (e) {
        // Fallback for older browsers
        const input = document.createElement('input');
        input.value = this.linkUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      }
    },

    formatSmsTimestamp(ts) {
      if (!ts) return '';
      const ms = ts.seconds ? ts.seconds * 1000 : ts._seconds ? ts._seconds * 1000 : new Date(ts).getTime();
      const d = new Date(ms);
      return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
             d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    },

    initMap() {
      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: "mapbox://styles/mapbox/dark-v11",
        center: [153.23, -27.73],
        zoom: 10,
      });
      this.map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

      // Click map background to clear job focus
      this.map.on('click', () => {
        if (this.focusedContractorId) {
          this.clearFocus();
        }
      });
    }
  },

  mounted() {
    this.initMap();
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.selectedDate = `${yyyy}-${mm}-${dd}`;
  },

  beforeDestroy() {
    this.clearMapLayers();
    if (this.map) this.map.remove();
  }
};
</script>

<style scoped>
.header-bold {
  font-weight: 700;
}
.report-slide-enter-active,
.report-slide-leave-active {
  transition: max-height 0.35s ease, opacity 0.3s ease;
  overflow: hidden;
}
.report-slide-enter,
.report-slide-leave-to {
  max-height: 0 !important;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.report-slide-enter-to,
.report-slide-leave {
  max-height: 50vh;
  opacity: 1;
}
</style>
